import React, { useState, useEffect, useRef } from "react";
import {
  generateSolutionGrid,
  acrossClues,
  downClues,
  gridSize,
} from "../data/crosswordData";
import "./MiniCrossword.css";

function MiniCrossword() {
  const [solutionGrid, setSolutionGrid] = useState([]);
  const [userGrid, setUserGrid] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState("across");
  const [selectedClue, setSelectedClue] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const inputRefs = useRef({});
  const audioRef = useRef(null);

  useEffect(() => {
    initializeGrid();
  }, []);

  const focusCell = (row, col) => {
    const key = `${row}-${col}`;
    if (inputRefs.current[key]) {
      inputRefs.current[key].focus();
      setSelectedCell({ row, col });
    }
  };

  const updateCellValue = (row, col, value) => {
    setUserGrid((prev) =>
      prev.map((rowCells, r) =>
        rowCells.map((cell, c) => {
          if (r === row && c === col && !cell?.isBlock) {
            return { ...cell, value };
          }
          return cell;
        })
      )
    );
  };

  const getNextPlayableCell = (row, col, direction, delta) => {
    let newRow = row;
    let newCol = col;

    while (true) {
      if (direction === "across") {
        newCol += delta;
      } else {
        newRow += delta;
      }

      if (
        newRow < 0 ||
        newRow >= gridSize ||
        newCol < 0 ||
        newCol >= gridSize
      ) {
        return null;
      }

      if (!solutionGrid[newRow][newCol]?.isBlock) {
        return { row: newRow, col: newCol };
      }
    }
  };

  const getWordBounds = (cell, direction) => {
    if (!cell || !solutionGrid.length) return null;
    const { row, col } = cell;

    if (direction === "across") {
      let startCol = col;
      let endCol = col;
      while (startCol - 1 >= 0 && !solutionGrid[row][startCol - 1]?.isBlock) {
        startCol--;
      }
      while (endCol + 1 < gridSize && !solutionGrid[row][endCol + 1]?.isBlock) {
        endCol++;
      }
      return { startRow: row, endRow: row, startCol, endCol };
    }

    let startRow = row;
    let endRow = row;
    while (startRow - 1 >= 0 && !solutionGrid[startRow - 1][col]?.isBlock) {
      startRow--;
    }
    while (endRow + 1 < gridSize && !solutionGrid[endRow + 1][col]?.isBlock) {
      endRow++;
    }
    return { startRow, endRow, startCol: col, endCol: col };
  };

  const initializeGrid = () => {
    const solution = generateSolutionGrid();
    setSolutionGrid(solution);

    // Initialize empty user grid
    const emptyGrid = Array(gridSize)
      .fill(null)
      .map((_, row) =>
        Array(gridSize)
          .fill(null)
          .map((_, col) => {
            if (solution[row][col]?.isBlock) {
              return { isBlock: true };
            }
            return { value: "", isBlock: false };
          })
      );
    setUserGrid(emptyGrid);
    setIsComplete(false);
    setShowSuccess(false);
  };

  const handleCellClick = (row, col) => {
    if (solutionGrid[row][col]?.isBlock) return;

    // If clicking the same cell, toggle direction
    if (selectedCell && selectedCell.row === row && selectedCell.col === col) {
      setSelectedDirection(selectedDirection === "across" ? "down" : "across");
    }

    focusCell(row, col);
  };

  const handleCellChange = (row, col, value) => {
    updateCellValue(row, col, value.toUpperCase());

    // Move to next cell if a letter was entered
    if (value) {
      moveToNextCell(row, col);
    }
  };

  const handleKeyDown = (e, row, col) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (userGrid[row][col].value) {
        updateCellValue(row, col, "");
      } else {
        const previousCell = getNextPlayableCell(
          row,
          col,
          selectedDirection,
          -1
        );
        if (previousCell) {
          updateCellValue(previousCell.row, previousCell.col, "");
          focusCell(previousCell.row, previousCell.col);
        }
      }
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setSelectedDirection("across");
      moveInDirection(row, col, "across", 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSelectedDirection("across");
      moveInDirection(row, col, "across", -1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedDirection("down");
      moveInDirection(row, col, "down", 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedDirection("down");
      moveInDirection(row, col, "down", -1);
    }
  };

  const moveToNextCell = (row, col) => {
    if (selectedDirection === "across") {
      moveInDirection(row, col, "across", 1);
    } else {
      moveInDirection(row, col, "down", 1);
    }
  };

  const moveInDirection = (row, col, direction, delta) => {
    const nextCell = getNextPlayableCell(row, col, direction, delta);
    if (nextCell) {
      focusCell(nextCell.row, nextCell.col);
    }
  };

  const handleClueClick = (clue, direction) => {
    setSelectedClue(clue.number);
    setSelectedDirection(direction);
    focusCell(clue.row, clue.col);
  };

  const checkSolution = () => {
    let correct = true;
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        if (!solutionGrid[row][col]?.isBlock) {
          if (userGrid[row][col].value !== solutionGrid[row][col].letter) {
            correct = false;
            break;
          }
        }
      }
      if (!correct) break;
    }

    if (correct) {
      setIsComplete(true);
      setShowSuccess(true);
      // Play victory music
      if (audioRef.current) {
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current
          .play()
          .catch((e) => console.log("Audio play failed:", e));
      }
    } else {
      alert("Not quite yet! Keep trying! 💪");
    }
  };

  const isCellInSelectedWord = (row, col) => {
    if (!selectedCell) return false;
    const bounds = getWordBounds(selectedCell, selectedDirection);
    if (!bounds) return false;

    if (selectedDirection === "across") {
      return (
        row === selectedCell.row &&
        col >= bounds.startCol &&
        col <= bounds.endCol
      );
    }
    return (
      col === selectedCell.col && row >= bounds.startRow && row <= bounds.endRow
    );
  };

  return (
    <div className="mini-crossword">
      <div className="crossword-intro">
        <p>
          Enjoy this very ugly crossword because I suck at Frontend Development,
          ignore the bugs and lower down the volume
        </p>
      </div>

      {showSuccess && (
        <div className="success-message">
          🎉 Perfect! You know us so well! 🎉
        </div>
      )}

      <div className="crossword-container">
        <div className="crossword-grid" style={{ "--grid-size": gridSize }}>
          {solutionGrid.map((row, rowIndex) => (
            <div key={rowIndex} className="crossword-row">
              {row.map((cell, colIndex) => {
                if (cell?.isBlock) {
                  return (
                    <div key={colIndex} className="crossword-cell block" />
                  );
                }

                const isSelected =
                  selectedCell?.row === rowIndex &&
                  selectedCell?.col === colIndex;
                const isInWord = isCellInSelectedWord(rowIndex, colIndex);

                return (
                  <div
                    key={colIndex}
                    className={`crossword-cell ${
                      isSelected ? "selected" : ""
                    } ${isInWord ? "highlighted" : ""}`}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    {cell?.number && (
                      <span className="cell-number">{cell.number}</span>
                    )}
                    <input
                      ref={(el) =>
                        (inputRefs.current[`${rowIndex}-${colIndex}`] = el)
                      }
                      type="text"
                      maxLength="1"
                      value={userGrid[rowIndex]?.[colIndex]?.value || ""}
                      onChange={(e) =>
                        handleCellChange(rowIndex, colIndex, e.target.value)
                      }
                      onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                      onFocus={() =>
                        setSelectedCell({ row: rowIndex, col: colIndex })
                      }
                      disabled={isComplete}
                      className="cell-input"
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="clues-container">
          <div className="clues-section">
            <h3>Across</h3>
            <ul className="clues-list">
              {acrossClues.map((clue) => (
                <li
                  key={clue.number}
                  className={`clue-item ${
                    selectedClue === clue.number &&
                    selectedDirection === "across"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleClueClick(clue, "across")}
                >
                  <strong>{clue.number}.</strong> {clue.clue}
                </li>
              ))}
            </ul>
          </div>

          <div className="clues-section">
            <h3>Down</h3>
            <ul className="clues-list">
              {downClues.map((clue) => (
                <li
                  key={clue.number}
                  className={`clue-item ${
                    selectedClue === clue.number && selectedDirection === "down"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleClueClick(clue, "down")}
                >
                  <strong>{clue.number}.</strong> {clue.clue}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="crossword-controls">
        <button
          className="control-button"
          onClick={checkSolution}
          disabled={isComplete}
        >
          Check Answer
        </button>
        <button className="control-button" onClick={initializeGrid}>
          Reset Puzzle
        </button>
      </div>

      {/* Victory Audio */}
      <audio ref={audioRef} preload="auto" style={{ display: "none" }}>
        {/* Add your CSGO music file to public/victory-music.mp3 */}
        <source src="/victory-music.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
}

export default MiniCrossword;
